import * as XLSX from 'xlsx';

// Client-side spreadsheet handling for Configuration > Customer Access's bulk import (Phase E).
// The API never sees the file -- only the rows this module parses out of it -- so there's no
// upload endpoint and no base64 payload; `xlsx` is already a dependency here for the export screens
// (TimeReports.js, BPATracker.js), so this is the read direction of the same library, not a new one.

export const IMPORT_TEMPLATE_HEADERS = ['Name', 'Email', 'Project', 'Role', 'Office', 'Department', 'Phone'];

const EXAMPLE_ROW = [
  'Jane Smith',
  'jane.smith@example.gov',
  'Example County - Hosting Project',
  'County IT Director',
  'County Seat',
  'IT',
  '(555) 555-0100',
];

/// Triggers a browser download of a starting-point spreadsheet -- one example row, not a blank
/// sheet, so the expected shape is obvious without reading a column-header glossary first.
export function downloadImportTemplate() {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([IMPORT_TEMPLATE_HEADERS, EXAMPLE_ROW]);
  ws['!cols'] = [{ wch: 20 }, { wch: 30 }, { wch: 32 }, { wch: 24 }, { wch: 16 }, { wch: 16 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(wb, ws, 'Import');
  XLSX.writeFile(wb, 'Customer Access Import Template.xlsx');
}

const norm = (value) => (value ?? '').toString().trim().toLowerCase();

/// Case/whitespace-tolerant header lookup, not an exact key match -- a county filling this in by
/// hand will retype "email" or "e-mail" as often as they'll leave "Email" alone, and reordering or
/// dropping an optional column shouldn't break the import.
function field(row, label) {
  const key = Object.keys(row).find((k) => norm(k) === norm(label));
  const value = key ? row[key] : null;
  return value == null ? '' : String(value).trim();
}

/**
 * Reads a File into parsed rows. Resolves to an empty array for a spreadsheet with no data rows
 * (the caller shows "no rows found" as a normal message) and rejects only when the file itself
 * can't be parsed as a spreadsheet at all.
 */
export function readImportFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read this file.'));
    reader.onload = () => {
      try {
        const workbook = XLSX.read(reader.result, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!sheet) return resolve([]);
        const raw = XLSX.utils.sheet_to_json(sheet, { defval: null });
        const rows = raw
          .map((r, i) => ({
            rowNumber: i + 2, // header is row 1
            name: field(r, 'Name'),
            email: field(r, 'Email').toLowerCase(),
            projectName: field(r, 'Project'),
            role: field(r, 'Role') || null,
            office: field(r, 'Office') || null,
            department: field(r, 'Department') || null,
            phone: field(r, 'Phone') || null,
          }))
          .filter((r) => r.name || r.email || r.projectName);
        resolve(rows);
      } catch {
        reject(new Error('Could not read this file — make sure it is a .xlsx spreadsheet.'));
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

/// Strips a preview row (which carries API-computed fields: errors, projectId, userAction, ...)
/// back down to the plain shape `commit` accepts. The API's ValidationPipe runs with
/// `forbidNonWhitelisted`, so sending the extra fields back would 400 rather than being ignored.
export function toCommitRow(evaluatedRow) {
  const { rowNumber, name, email, projectName, role, office, department, phone } = evaluatedRow;
  return { rowNumber, name, email, projectName, role, office, department, phone };
}

export const USER_ACTION_LABELS = {
  create: 'New contact',
  grant: 'Existing contact — new project access',
  'already-has-access': 'Already has access',
  skip: 'Error',
};

export const USER_ACTION_BADGE = {
  create: 'badge-green',
  grant: 'badge-blue',
  'already-has-access': 'badge-gray',
  skip: 'badge-yellow',
};
